import { z } from 'zod';
import { baseComponentSchema, imageCropSchema } from '../component-base.schemas';

// Media-text component schemas
export const mediaTextButtonSchema = z.object({
  id: z.string().uuid().optional(),
  text: z.string().min(1, 'Button text is required').max(50, 'Button text is too long'),
  url: z.string().min(1, 'Button URL is required'),
  style: z.enum(['primary', 'secondary', 'outline', 'ghost']).default('primary'),
  size: z.enum(['small', 'medium', 'large']).default('medium'),
  target: z.enum(['_blank', '_self']).default('_self').optional(),
  order: z.number().int().min(0).optional(),
});

export const mediaTextMediaSchema = z
  .object({
    type: z.enum(['image', 'video']),
    url: z.string().min(1, 'Media URL or video ID is required'),
    alt: z.string().max(200, 'Alt text must be less than 200 characters').optional(),

    // Video source type: upload (self-hosted), youtube, or vimeo
    sourceType: z.enum(['upload', 'youtube', 'vimeo']).default('upload').optional(),

    // Original embed URL (for display purposes in editor)
    embedUrl: z.string().optional(),

    // Upload metadata
    mediaId: z.string().uuid().optional(),
    s3Key: z.string().optional(),
    cloudfrontUrl: z.string().url().optional(),

    // Dimensions
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    aspectRatio: z.enum(['16:9', '4:3', '1:1', '3:2', 'auto']).default('auto').optional(),

    // Image styling
    borderRadius: z.enum(['none', 'small', 'medium', 'large', 'circle']).default('none').optional(),
    border: z
      .object({
        enabled: z.boolean().default(false),
        width: z.number().int().min(1).max(10).default(1).optional(),
        color: z
          .string()
          .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
          .default('#000000')
          .optional(),
        style: z.enum(['solid', 'dashed', 'dotted']).default('solid').optional(),
      })
      .optional(),
    shadow: z
      .object({
        enabled: z.boolean().default(false),
        size: z.enum(['small', 'medium', 'large']).default('medium').optional(),
        color: z
          .string()
          .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
          .default('#000000')
          .optional(),
      })
      .optional(),

    // Video-specific
    autoplay: z.boolean().default(false).optional(),
    muted: z.boolean().default(true).optional(),
    controls: z.boolean().default(true).optional(),
    loop: z.boolean().default(false).optional(),
    poster: z.string().url('Invalid poster URL').optional(),

    // Video thumbnail (for uploaded videos)
    thumbnail: z
      .object({
        url: z.string().min(1),
        cloudfrontUrl: z.string().url().optional(),
        mediaId: z.string().uuid().optional(),
        alt: z.string().optional(),
      })
      .optional(),

    // Image crop data
    crop: imageCropSchema,
  })
  .refine(
    data => {
      // Video-specific fields should only be present for video type
      if (data.type === 'image') {
        return (
          !data.autoplay &&
          !data.muted &&
          !data.controls &&
          !data.loop &&
          !data.poster &&
          !data.sourceType &&
          !data.embedUrl
        );
      }
      return true;
    },
    {
      message: 'Video-specific properties are only allowed for video media type',
    }
  );

export const mediaTextComponentSchema = baseComponentSchema.extend({
  type: z.literal('media-text'),
  data: z
    .object({
      // Content
      title: z
        .string()
        .min(1, 'Title is required')
        .max(200, 'Title must be less than 200 characters'),
      subtitle: z.string().max(300, 'Subtitle must be less than 300 characters').optional(),
      content: z
        .string()
        .min(1, 'Content is required')
        .max(5000, 'Content must be less than 5000 characters'),

      // Media
      media: mediaTextMediaSchema,

      // Layout
      layout: z.enum(['media-left', 'media-right']).default('media-left'),
      alignment: z.enum(['top', 'center', 'bottom']).default('center'),
      spacing: z.enum(['compact', 'normal', 'spacious']).default('normal'),

      // Responsive
      stackOnMobile: z.boolean().default(true),
      mobileMediaFirst: z.boolean().default(true),

      // Buttons
      buttons: z.array(mediaTextButtonSchema).max(3, 'Maximum 3 buttons allowed').default([]),

      // Column sizing (percentages)
      mediaWidth: z.number().int().min(20).max(80).default(45),
      textWidth: z.number().int().min(20).max(80).default(55),

      // Styling
      backgroundColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
        .optional(),
      textColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
        .optional(),
      padding: z
        .object({
          top: z.number().int().min(0).max(200).default(60),
          bottom: z.number().int().min(0).max(200).default(60),
          left: z.number().int().min(0).max(200).default(20),
          right: z.number().int().min(0).max(200).default(20),
        })
        .default({
          top: 60,
          bottom: 60,
          left: 20,
          right: 20,
        }),
      titleColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
        .optional(),
      titleSize: z.enum(['small', 'medium', 'large', 'xl']).default('large').optional(),
      subtitleColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid color format')
        .optional(),
      // Animation
      animateOnScroll: z.boolean().default(false).optional(),
      animationDelay: z.number().int().min(0).max(2000).default(0).optional(),
    })
    .refine(
      data => {
        // Media width + text width should equal 100
        return data.mediaWidth + data.textWidth === 100;
      },
      {
        message: 'Media width and text width must add up to 100%',
      }
    ),
});

// Type exports
export type MediaTextButton = z.infer<typeof mediaTextButtonSchema>;
export type MediaTextMedia = z.infer<typeof mediaTextMediaSchema>;
export type MediaTextComponent = z.infer<typeof mediaTextComponentSchema>;
